import { create } from 'zustand';
import { keycloak } from '../lib/keycloak';

export interface User {
  id: number;
  keycloak_id: string;
  email: string;
  name: string;
  role: 'admin' | 'vendor' | 'customer';
  avatar_url: string;
  is_vendor_approved: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: () => Promise<void>;
  logout: () => Promise<void>;
  fetchUserProfile: () => Promise<void>;
  setAuthData: (user: User, token: string) => void;
  clearAuth: () => void;
  setError: (error: string | null) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async () => {
    if (get().isLoading) return;

    set({ isLoading: true, error: null });
    try {
      await keycloak.login({
        redirectUri: `${window.location.origin}/`,
        prompt: 'login',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  logout: async () => {
    try {
      await keycloak.logout({
        redirectUri: `${window.location.origin}/`,
      });
    } catch (error) {
      console.error('Keycloak logout failed:', error);
    }
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
  },

  fetchUserProfile: async () => {
    const token = keycloak.token ?? null;
    if (!token) {
      set({ isAuthenticated: false, user: null, token: null });
      return;
    }
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/auth/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to load profile');
      }
      const profile = await response.json();
      set({
        user: profile.user as User,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load profile';
      set({ isAuthenticated: false, user: null, token: null, error: errorMessage });
    }
  },

  setAuthData: (user, token) => {
    set({
      user,
      token,
      isAuthenticated: true,
      error: null,
      isLoading: false,
    });
  },

  clearAuth: () => {
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
      isLoading: false,
    });
  },

  setError: (error) => set({ error }),
}));

export function useAuth() {
  const store = useAuthStore();

  return {
    user: store.user,
    token: store.token,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    login: store.login,
    logout: store.logout,
  };
}
